import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { offerSelectedTier } from 'fym-shared/src/music24/watermarkUpsell';
import { MUSIC24_MONTHLY_PRICE_USD } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { planNameMessages } from '../../config/music24PlanNames';
import { useMusic24Plan } from '../../hooks/useMusic24Plan';
import { formatPlanPrice, useDisplayCurrency } from '../../config/useDisplayCurrency';
import { watermarkChoiceMessages } from '../choice/watermarkChoiceMessages';
import { SHAPE_SIZES } from '../files/watermarkedFile';
import { WatermarkThumbTile } from '../files/WatermarkThumbTile';
import { useOpenWatermarkOffer } from '../useOpenWatermarkOffer';
import { useUpsellEventFacts } from '../useUpsellEventFacts';
import { useUpsellShown } from '../useUpsellShown';
import { useWatermarkSummary } from '../useWatermarkSummary';
import { trackUpsellDismissed } from '../watermarkUpsellAnalytics';
import { watermarkTrialMessages as messages } from '../trial/watermarkTrialMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FirstFileRevealProps {
  onClose: () => void;
}

/**
 * The before and after of the first watermarked file (design: trial-upsell a1), one time for each
 * owner. The left tile is the real file, with the real Music24 mark. The right tile is the same
 * picture with its foot, where the mark sits, cut off: the web app has no clean render of the file
 * before the payment. "Remove the watermark" opens the offer (b4).
 */
const FirstFileRevealComponent: React.FC<FirstFileRevealProps> = ({ onClose }) => {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const { summary } = useWatermarkSummary();
  const openOffer = useOpenWatermarkOffer();
  const eventFacts = useUpsellEventFacts();
  // The reveal also opens on Free: there the copy must not name a trial.
  const isTrial = useMusic24Plan() === 'trial';
  useUpsellShown('first_file', true);
  const remove = useCallback(() => {
    onClose();
    openOffer('first_file');
  }, [onClose, openOffer]);
  const dismiss = useCallback(() => {
    trackUpsellDismissed(eventFacts('first_file'));
    onClose();
  }, [eventFacts, onClose]);

  // A story, portrait or square file has the mark at its foot, so the "on a plan" tile can cut it
  // off. A wide file has it beside the title: use it only when there is no other (QA mobr2-2).
  const thumb = summary.thumbs.find((entry) => entry.shape !== 'wide') ?? summary.thumbs[0] ?? null;
  const isMany = summary.files > 1;
  let title: string;
  if (isMany) {
    title =
      summary.songTitle === null
        ? intl.formatMessage(messages.firstTitleManyNoSong, { count: summary.files })
        : intl.formatMessage(messages.firstTitleMany, { count: summary.files, song: summary.songTitle });
  } else {
    title =
      summary.songTitle === null
        ? intl.formatMessage(messages.firstTitleNoSong)
        : intl.formatMessage(messages.firstTitle, { song: summary.songTitle });
  }
  const shape = thumb?.shape ?? 'story';
  const sizes = SHAPE_SIZES[shape];
  const tier = offerSelectedTier(summary);
  const price = formatPlanPrice(intl, MUSIC24_MONTHLY_PRICE_USD[tier] * 100, currency);

  return (
    <Modal
      isOpen
      toggle={dismiss}
      centered
      className={clsx(styles.dialog, styles.dialogReveal)}
      contentClassName={styles.content}
      data-testid="first-file-reveal"
    >
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.close}
          onClick={dismiss}
          aria-label={intl.formatMessage(watermarkChoiceMessages.close)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <div className={styles.eyebrow}>
          {intl.formatMessage(isMany ? messages.firstEyebrowMany : messages.firstEyebrow)}
        </div>
        <div>
          <h3 className={styles.h}>{title}</h3>
          <p className={styles.p}>
            {withBrandWord(intl.formatMessage(isTrial ? messages.firstBody : messages.firstBodyFree))}
          </p>
        </div>
        <div className={styles.ba}>
          <div className={styles.side}>
            <span className={styles.sideKey}>
              {intl.formatMessage(isTrial ? messages.firstNow : messages.firstNowFree)}
            </span>
            <WatermarkThumbTile url={thumb?.url ?? null} shape={shape} marked />
            <span className={styles.sideValue}>
              {intl.formatMessage(messages.firstNowValue, { width: sizes.preview.width, height: sizes.preview.height })}
            </span>
          </div>
          <span className={styles.arrow} aria-hidden="true">
            →
          </span>
          <div className={styles.side}>
            <span className={clsx(styles.sideKey, styles.sideKeyOn)}>{intl.formatMessage(messages.firstPlan)}</span>
            <WatermarkThumbTile url={thumb?.url ?? null} shape={shape} clean />
            <span className={styles.sideValue}>
              {intl.formatMessage(messages.firstPlanValue, { width: sizes.full.width, height: sizes.full.height })}
            </span>
          </div>
        </div>
        <div className={styles.acts}>
          <SMLButton variant="primary" onClick={remove}>
            {intl.formatMessage(watermarkChoiceMessages.remove)}
          </SMLButton>
          <span className={styles.fine}>
            {intl.formatMessage(messages.firstFine, { plan: intl.formatMessage(planNameMessages[tier]), price })}
          </span>
          <button type="button" className={styles.later} onClick={dismiss}>
            {intl.formatMessage(messages.firstKeep)}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const FirstFileReveal = TypedMemo(FirstFileRevealComponent);
